import { IconButton, Menu, Tooltip } from "@itk/mui-components";
import { Box, Stack, Theme, Typography } from "@mui/material";
import { useTreeItemUtils } from "@mui/x-tree-view/hooks";
import { useMemo, useState } from "react";
import { DekoxIcon } from "src/components";
import { getClickableProps } from "src/utils/keyboard-utils";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { Model_ConfigurationGet_SystemProductConfigurationItem } from "src/backend/openapi/v1";
import { CopyMenu } from "src/pages/configurator/components";

type Props = {
  item: Model_ConfigurationGet_SystemProductConfigurationItem;
  isSelected: boolean;
  onSelect: () => void;
  onAddChild: () => void;
  onDelete: () => void;
  onCopy: (blankValues: boolean, count: number) => void;
  maxCount?: number;
  canAddChild: boolean;
  canCopy: boolean;
  theme: Theme;
};

// One tree row: leading icon + product text, with a hover-only add button instead of an
// always-visible full-width button, and a persistent border+overlay when selected.
export const ProductLineItemRow = (props: Props) => {
  const { item, isSelected, onSelect, onAddChild, onDelete, onCopy, maxCount, canAddChild, canCopy, theme } = props;

  // Drives our own inline chevron — the native TreeItem icon (rendered outside this row's box) is hidden via sx
  const { interactions, status } = useTreeItemUtils({ itemId: item.systemProductConfigurationItemId ?? "" });

  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  const [copyBtnAnchor, setCopyBtnAnchor] = useState<HTMLButtonElement | null>(null);

  const missingAttributes = useMemo(() => (item.attributes ?? []).filter(attribute => attribute.isRequired && !attribute.disabled && attribute.value.length === 0), [item.attributes]);

  const invalidAttributes = useMemo(() => (item.attributes ?? []).filter(attribute => attribute.isValid === false), [item.attributes]);

  const isMissing = missingAttributes.length > 0;
  const isInvalid = item.isValid === false || invalidAttributes.length > 0;
  const hasIssue = isMissing || isInvalid;

  return (
    <Box
      {...getClickableProps(onSelect)}
      onClick={onSelect}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: "100%",
        p: 1,
        cursor: "pointer",
        outline: "none",
        borderRadius: 1,
        border: isSelected ? `1px solid ${theme.palette.customColors.whiteBorderStrong}` : "none",
        bgcolor: isSelected ? theme.palette.customColors.darkGreen : theme.palette.customColors.green,
        color: theme.palette.primary.contrastText,
        "&:focus-visible": { outline: `2px solid ${theme.palette.primary.contrastText}` },
        // Darkens the row on hover — set on this box (not the TreeView wrapper) so it's clipped to this border, never overflowing it
        "&:hover": { backgroundColor: `${theme.palette.customColors.black}26` },
        // At rest the actions container is 0-width so the text gets the full row; on hover it grows
        // into view (and the text's flex:1 shrinks to make room) instead of always reserving the space.
        // Sized for up to 3 buttons (add + copy + delete) since canAddChild rows show all three.
        "&:hover .product-line-item-actions": { maxWidth: 140, ml: 1 }
      }}
    >
      {status.expandable && (
        <DekoxIcon
          icon={status.expanded ? "arrow-up" : "arrow-down"}
          fontSize="small"
          onClick={e => {
            e.stopPropagation();
            interactions.handleExpansion(e);
          }}
          sx={{ color: theme.palette.primary.contrastText, flexShrink: 0, mr: 1 }}
        />
      )}
      <Typography variant="subtitle2" fontWeight="500" flex={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
        {item.productDescription} ({item.productNumber})
      </Typography>
      {/* Combined missing/invalid indicator — only mounted while active, so it doesn't reserve layout space otherwise.
          Built as a solid circle + a separate white exclamation glyph on top (rather than the single-path
          "error" icon, whose mark is a transparent cutout) so the "!" stays crisp white regardless of the
          row's own background color. */}
      {hasIssue && (
        <Tooltip
          title={
            <Box>
              {isMissing && (
                <Box>
                  <Box>{configuratorPageT.productIsMissingInformation}</Box>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {missingAttributes.map((attribute, index) => (
                      <li key={index}>{attribute.description}</li>
                    ))}
                  </Box>
                </Box>
              )}
              {isInvalid && (
                <Box>
                  <Box>{configuratorPageT.productHasInvalidInformation}</Box>
                  {invalidAttributes.length > 0 && (
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {invalidAttributes.map((attribute, index) => (
                        <li key={index}>{attribute.description}</li>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          }
        >
          <Box
            sx={{
              ml: 1,
              flexShrink: 0,
              width: 20,
              height: 20,
              borderRadius: "50%",
              bgcolor: theme.palette.customColors.errorOnPrimary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <DekoxIcon icon="priorityHigh" sx={{ fontSize: 14, color: theme.palette.customColors.white }} />
          </Box>
        </Tooltip>
      )}
      {/* Two separate circular icon buttons. 0-width (no reserved space) until row hover, when it
          grows into view — each button's own background contrasts with the row's own current background. */}
      <Stack direction="row" gap={1} className="product-line-item-actions" sx={{ maxWidth: 0, ml: 0, overflow: "hidden", flexShrink: 0, transition: "max-width 0.2s, margin-left 0.2s" }}>
        {canAddChild && (
          <>
            {/* Tooltip names the product type being added, since the icon-only button has no visible label */}
            <Tooltip title={item.nextProductTypeName ?? ""}>
              <IconButton
                k={genericTranslationsK.add}
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  onAddChild();
                }}
                sx={{
                  bgcolor: isSelected ? theme.palette.customColors.green : theme.palette.customColors.darkGreen,
                  color: theme.palette.customColors.white,
                  "&:hover": { bgcolor: `${theme.palette.customColors.black}26` }
                }}
              >
                <DekoxIcon icon="add" fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        {/* Copy is independent of "+"'s availability — shown only one level below a root, not on root items and not deeper */}
        {canCopy && (
          <>
            <Tooltip title={genericTranslationsT.copy}>
              <IconButton
                k={genericTranslationsK.copy}
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setCopyBtnAnchor(e.currentTarget);
                }}
                sx={{
                  bgcolor: isSelected ? theme.palette.customColors.green : theme.palette.customColors.darkGreen,
                  color: theme.palette.customColors.white,
                  "&:hover": { bgcolor: `${theme.palette.customColors.black}26` }
                }}
              >
                <DekoxIcon icon="copy" fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={copyBtnAnchor} open={Boolean(copyBtnAnchor)} onClose={() => setCopyBtnAnchor(null)}>
              <CopyMenu
                onCopy={(blankValues, count) => {
                  setCopyBtnAnchor(null);
                  onCopy(blankValues, count);
                }}
                maxCount={maxCount}
              />
            </Menu>
          </>
        )}
        <IconButton
          k={genericTranslationsK.delete}
          size="small"
          onClick={e => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{
            bgcolor: isSelected ? theme.palette.customColors.green : theme.palette.customColors.darkGreen,
            color: theme.palette.customColors.white,
            "&:hover": { bgcolor: `${theme.palette.customColors.black}26` }
          }}
        >
          <DekoxIcon icon="delete" fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
};
